/**
 * Die Faelligkeitszelle der Sammelsendungs-Elternzeile (Plan 2026-10-31, Task 6): mit dem Recht `edit_orders` ein Klick
 * darauf oeffnet ein Datumsfeld, das Speichern setzt das Datum zentral fuer die ganze Sendung (`onSetzen`, App.tsx
 * schreibt ueber `api/sammelsendungen.ts#gemeinsameWerteSchreiben` und laedt die Kinder nach).
 *
 * ================= Was wann schreibt (Fix-Runde 1) =================
 *
 * Getippt oder gewaehlt wird nur der ENTWURF (`onChange`); geschrieben wird erst bei Enter oder beim Verlassen des Feldes
 * (`blur`). Grund: ein `<input type="date">` meldet je getipptem Segment ein gueltiges Zwischendatum. Wer im Tag "31" tippt,
 * liefert zuerst den 03., wer in ein leeres Feld "15012027" tippt, zuerst `0002-01-15`; jedes `change` zu schreiben haette
 * das Datum zentral auf ALLE Mitglieder gesetzt. Dasselbe Muster, aus demselben Grund, steht in `Drawer/Datumsfeld.tsx`
 * (Kopfkommentar, Asana 1218053584680823) und in der Spec (Z. 48: "schreiben nur, wenn man wirklich ein anderes Datum waehlt").
 *
 * - Enter uebernimmt: ein gueltiger Entwurf, der vom Startwert abweicht, geht an `onSetzen(datum)`. Ein LEERER Entwurf
 *   geht als `onSetzen(null)` hinaus, sofern ein zentrales Datum gesetzt ist (das Leeren ist die einzige Aktion, die
 *   ein bewusstes Enter verlangt) und das Feld wirklich leer ist; ein halb ueberschriebenes Datum (Wert "" bei
 *   `validity.badInput`) leert nicht. Sonst schliesst Enter nur. Der Fokus kehrt an die Zelle zurueck.
 * - `blur` uebernimmt wie Enter, nur ein leerer Entwurf schreibt dort NICHT: ein Datumsfeld meldet auch ein halb
 *   ueberschriebenes Datum als "", und das Verlassen darf keine Sendung leeren.
 * - Escape verwirft den Entwurf und schliesst; der Fokus kehrt an die Zelle zurueck.
 * - Gueltig ist, was `istGueltigesFaelligkeitsdatum` (`lib/faelligkeitsdatum.ts`) annimmt, dieselbe Regel wie im Drawer. Ein
 *   Entwurf wie `0002-01-15` (Zwischenstand der ersten Jahresziffer) oder `20261-12-31` schliesst das Feld ohne zu schreiben.
 * - Genau ein Schreiben je Eingabe: Enter und Escape setzen `beendet`, ein darauf folgendes `blur` (manche Browser melden
 *   es beim Abbau des fokussierten Feldes) tut nichts.
 *
 * ================= Der Klick gehoert der Zelle =================
 *
 * Alle Klicks und Tasten in Zelle und Feld enden hier (`stopPropagation`, wie `Bestellzeile.tsx#Bearbeitbar`): ueber der
 * Elternzeile haengen Zeilenauswahl und Tastenkuerzel des Boards, und Escape darf nebenher nicht die Mehrfachauswahl leeren.
 * Die Huelle traegt dieselben Klassen wie eine bearbeitbare Zelle der Kinderzeile (`order-list-editable-value`), das
 * Feld die Klassen der Datumsspalte aus der Regel-Tabelle (`lib/orderListEditor.ts`): Aussehen aus `bestand.css`, keine eigene CSS.
 *
 * Ohne `darfBearbeiten` gibt es nur die Anzeige (`inhalt`), keine Huelle und kein Feld.
 */
import { useEffect, useRef, useState } from "react";
import type { KeyboardEvent, MouseEvent, ReactNode } from "react";
import { istGueltigesFaelligkeitsdatum } from "../../lib/faelligkeitsdatum.ts";
import { orderListEditorRegel } from "../../lib/orderListEditor.ts";

export interface SammelFaelligkeitZelleProps {
  /** Die fertig gezeichnete Aggregatzelle (`AggregatZellen.tsx`): Datum, Gedankenstrich oder der Hinweis auf abweichende Kinder. */
  inhalt: ReactNode;
  /** Das zentrale Faelligkeitsdatum der Sendung ("YYYY-MM-DD"); `null`: keins gesetzt. */
  zentral: string | null;
  dsvShipmentNo: string;
  darfBearbeiten: boolean;
  /** Ein neues Datum ("YYYY-MM-DD") oder `null` zum Leeren. */
  onSetzen: (wert: string | null) => void;
}

interface Editor {
  /** Der Wert beim Oeffnen: gegen ihn wird "abweichend" entschieden. */
  start: string;
  /** Der Entwurf: was das Feld gerade zeigt. */
  wert: string;
}

/** Die Klassen der Datumsspalte "Faelligkeit" aus der Regel-Tabelle; "due" ist nie gesperrt, braucht also keine Bestellung. */
const regel = orderListEditorRegel("due", null);
const FELD_KLASSE = regel.art === "editor" ? regel.regel.klasse : "";

const anhalten = (ereignis: MouseEvent): void => ereignis.stopPropagation();

/** Callback-Ref statt `autoFocus`: laeuft, sobald das Feld haengt, ohne Bildschirmtakt (wie `ZellenEditor`). */
const fokussieren = (knoten: HTMLInputElement | null): void => {
  knoten?.focus();
};

export function SammelFaelligkeitZelle({
  inhalt,
  zentral,
  dsvShipmentNo,
  darfBearbeiten,
  onSetzen,
}: SammelFaelligkeitZelleProps) {
  const [editor, setzeEditor] = useState<Editor | null>(null);
  const ausloeser = useRef<HTMLSpanElement | null>(null);
  // Nach Escape und nach Enter kehrt der Fokus zur Zelle zurueck; ein `blur` setzt das nicht.
  const fokusZurueck = useRef(false);
  // Die Eingabe ist durch Enter oder Escape beendet: ein spaeteres `blur` darf nicht noch einmal uebernehmen.
  const beendet = useRef(false);
  const geschlossen = editor === null;

  useEffect(() => {
    if (geschlossen && fokusZurueck.current) {
      fokusZurueck.current = false;
      ausloeser.current?.focus();
    }
  }, [geschlossen]);

  if (!darfBearbeiten) return <>{inhalt}</>;

  const name = `Fälligkeitsdatum für Sammelsendung ${dsvShipmentNo}`;

  if (editor === null) {
    const oeffnen = (): void => {
      beendet.current = false;
      setzeEditor({ start: zentral ?? "", wert: zentral ?? "" });
    };
    return (
      <span
        ref={ausloeser}
        className="order-list-editable-value is-block"
        role="button"
        tabIndex={0}
        aria-label={`${name} bearbeiten`}
        onClick={(ereignis) => {
          ereignis.stopPropagation();
          oeffnen();
        }}
        onContextMenu={anhalten}
        onKeyDown={(ereignis: KeyboardEvent) => {
          ereignis.stopPropagation();
          if (ereignis.key === "Enter" || ereignis.key === " ") {
            ereignis.preventDefault();
            oeffnen();
          }
        }}
      >
        {inhalt}
      </span>
    );
  }

  /** Schreibt den Entwurf, falls er etwas ist; `leerenErlaubt` nur bei Enter. Das Feld schliesst der Aufrufer. */
  const uebernehmen = (leerenErlaubt: boolean): void => {
    if (editor.wert === "") {
      if (leerenErlaubt && zentral) onSetzen(null);
    } else if (editor.wert !== editor.start && istGueltigesFaelligkeitsdatum(editor.wert)) {
      onSetzen(editor.wert);
    }
  };

  return (
    <input
      type="date"
      data-order-list-editor="true"
      className={FELD_KLASSE}
      aria-label={name}
      value={editor.wert}
      ref={fokussieren}
      onChange={(ereignis) => setzeEditor({ ...editor, wert: ereignis.currentTarget.value })}
      onBlur={() => {
        if (beendet.current) return;
        beendet.current = true;
        uebernehmen(false);
        setzeEditor(null);
      }}
      onClick={anhalten}
      onContextMenu={anhalten}
      onKeyDown={(ereignis) => {
        ereignis.stopPropagation();
        if (ereignis.key === "Escape") {
          ereignis.preventDefault();
          beendet.current = true;
          fokusZurueck.current = true;
          setzeEditor(null);
        } else if (ereignis.key === "Enter") {
          ereignis.preventDefault();
          beendet.current = true;
          // Ein Wert "" mit `badInput` ist ein halb ueberschriebenes Datum ("tt.12.2026"), kein bewusstes Leeren.
          uebernehmen(!ereignis.currentTarget.validity.badInput);
          fokusZurueck.current = true;
          setzeEditor(null);
        }
      }}
    />
  );
}
